import { useState } from "react";
import { X, Bell, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { subscribeNewsletter } from "../lib/api";

// Store is in Coming Soon mode — this drawer replaces the shopping cart
// experience with a launch-notification panel + newsletter capture.
export const CartDrawer = () => {
  const { isOpen, setIsOpen } = useCart();
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      await subscribeNewsletter(email.trim(), lang);
      setSubscribed(true);
      setEmail("");
      toast.success(t.newsletter.success);
    } catch {
      toast.error(t.newsletter.error);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <>
      <div
        data-testid="cart-backdrop"
        onClick={() => setIsOpen(false)}
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="cart-drawer"
        aria-hidden={!isOpen}
        className={`fixed top-0 right-0 h-full w-full sm:w-[460px] bg-black z-[70] border-l border-white/10 transform transition-transform duration-500 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-yellow-400 animate-pulse" />
            <h3 className="font-display text-xl uppercase tracking-tight">
              {t.comingSoon.drawerTitle}
            </h3>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            data-testid="cart-close-button"
            className="p-2 hover:text-yellow-400 transition-colors"
            aria-label={t.comingSoon.close}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-8 flex flex-col">
          {/* Coming Soon icon block */}
          <div className="flex items-center justify-center py-8">
            <div className="relative">
              <div className="absolute inset-0 bg-yellow-400/20 blur-2xl rounded-full" />
              <div className="relative w-20 h-20 border border-yellow-400/40 flex items-center justify-center">
                <Bell className="w-7 h-7 text-yellow-400" />
              </div>
            </div>
          </div>

          <p className="overline text-center mb-3">
            {t.comingSoon.badge}
          </p>
          <h4 className="h-display text-white text-3xl text-center leading-tight mb-4">
            {t.newsletter.title}
          </h4>
          <p className="text-sm text-white/60 leading-relaxed text-center max-w-sm mx-auto">
            {t.comingSoon.drawerBody}
          </p>

          {/* Newsletter capture */}
          <div className="mt-10">
            {subscribed ? (
              <div
                data-testid="drawer-notify-success"
                className="flex items-center gap-3 border border-yellow-400/40 bg-yellow-400/10 px-4 py-4"
              >
                <Check className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-yellow-400">
                    {t.newsletter.success}
                  </p>
                  <p className="text-xs text-white/60 mt-0.5">
                    {t.comingSoon.newsletterHint}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleNotify} className="space-y-3">
                <div className="flex items-center border border-white/20 focus-within:border-yellow-400 transition-colors">
                  <Mail className="w-4 h-4 mx-3 text-white/40" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.newsletter.placeholder}
                    data-testid="drawer-notify-input"
                    className="flex-1 bg-transparent py-3.5 text-sm focus:outline-none placeholder-white/40 pr-3"
                  />
                </div>
                <button
                  type="submit"
                  disabled={subscribing}
                  data-testid="drawer-notify-submit"
                  className="w-full bg-yellow-400 text-black py-4 font-bold uppercase tracking-[0.3em] text-xs hover:bg-yellow-300 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {subscribing ? "..." : t.newsletter.cta}
                </button>
              </form>
            )}
            <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-white/40 text-center">
              First drop · First access
            </p>
          </div>
        </div>

        <div className="border-t border-white/10 px-6 py-5">
          <button
            onClick={() => setIsOpen(false)}
            data-testid="drawer-continue"
            className="w-full text-center py-2 text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-yellow-400 transition-colors"
          >
            {t.cart.continue}
          </button>
        </div>
      </aside>
    </>
  );
};
